// Constantes de referencia (no se usan como datos estáticos, el mes/año se determinan dinámicamente)
export const MONTH = '';
export const YEAR = new Date().getFullYear();
export const DAYS_IN_MONTH = 31;
export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Julio 2026: ejemplo de referencia, se calcula dinámicamente con getDayLettersForMonth
export const DAY_LETTERS = ['X','J','V','S','D','L','M','X','J','V','S','D','L','M','X','J','V','S','D','L','M','X','J','V','S','D','L','M','X','J','V'];

export const WEEKDAYS_FULL = ['L','M','X','J','V','S','D'];

// Nombres de meses
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

/**
 * Genera las letras de día para un mes/año dado
 */
export function getDayLettersForMonth(mes, anio) {
  const daysInMonth = new Date(anio, mes, 0).getDate();
  const letters = ['D','L','M','X','J','V','S'];
  const result = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(anio, mes - 1, d);
    result.push(letters[date.getDay()]);
  }
  return result;
}

/**
 * Genera las semanas para un mes/año dado.
 * Cada semana siempre va de Lunes a Domingo.
 * Los días que no pertenecen al mes se incluyen como `null`
 * para que el render pueda mostrar la columna pero deshabilitada.
 *
 * Retorna:
 * [
 *   {
 *     label: 'Sem 1 (1–7)',
 *     days: [null, null, 1, 2, 3, 4, 5],  // null = fuera del mes
 *     validDays: [1, 2, 3, 4, 5],          // sólo los días reales
 *   },
 *   ...
 * ]
 */
export function getWeeksForMonth(mes, anio) {
  const daysInMonth = new Date(anio, mes, 0).getDate();

  // jsDay: 0=Dom,1=Lun,...,6=Sáb  →  weekIdx (Lun=0,...,Dom=6)
  const jsToWeekIdx = (jsDay) => (jsDay === 0 ? 6 : jsDay - 1);

  // Día de la semana del día 1 del mes (0=Lun, 6=Dom)
  const firstWeekIdx = jsToWeekIdx(new Date(anio, mes - 1, 1).getDay());

  // Construir array plano: null para días previos, 1..N para el mes
  const flat = [];
  for (let i = 0; i < firstWeekIdx; i++) flat.push(null);
  for (let d = 1; d <= daysInMonth; d++) flat.push(d);
  // Completar hasta múltiplo de 7
  while (flat.length % 7 !== 0) flat.push(null);

  const weeks = [];
  for (let w = 0; w < flat.length / 7; w++) {
    const slice = flat.slice(w * 7, w * 7 + 7); // 7 slots: Lun→Dom
    const validDays = slice.filter((d) => d !== null);
    if (!validDays.length) continue; // semana totalmente fuera del mes (no debería pasar)
    const first = validDays[0];
    const last = validDays[validDays.length - 1];
    weeks.push({
      label: `Sem ${weeks.length + 1} (${first}–${last})`,
      days: slice,       // 7 elementos, null donde no hay día del mes
      validDays,         // sólo los reales
    });
  }
  return weeks;
}

/**
 * Obtiene el nombre del mes
 */
export function getMonthName(mes) {
  return MESES[mes - 1] || '';
}

/**
 * Obtiene el índice de la semana actual para un mes/año.
 * Usa validDays (los días reales) para la comparación.
 */
export function getCurrentWeekIdx(mes, anio) {
  const today = new Date();
  if (today.getFullYear() === anio && today.getMonth() + 1 === mes) {
    const day = today.getDate();
    const weeks = getWeeksForMonth(mes, anio);
    for (let i = 0; i < weeks.length; i++) {
      const valid = weeks[i].validDays;
      if (day >= valid[0] && day <= valid[valid.length - 1]) return i;
    }
  }
  return 0;
}

// Los clientes se cargan del backend via API.
export const CLIENTES_CATALOGO = [];

// Las rutas se cargan del backend via API.
export const RUTAS = [];

/**
 * Calcula el FOC del vuelo a partir de los clientes asignados.
 * Normaliza los campos de kg/m3 independientemente de si vienen
 * del mock (kg/m3) o del backend (kg_asignado/m3_asignado/kgAsignado/m3Asignado).
 * 
 * Soporta `dias` como:
 *   - Objeto: { L: {check, kg, m3}, M: ... }
 *   - Array:  [{dia_semana:'L', kg_asignado:100, m3_asignado:1, check:true}, ...]
 */
export function calcFOC(vuelo) {
  const focKg = {};
  const focVol = {};

  // Normalizar dias de un cliente a objeto {L:{kg,m3,check}, ...}
  const normalizeDias = (dias) => {
    if (!dias) return {};
    // Ya es objeto con keys L/M/X/J/V/S/D
    if (!Array.isArray(dias) && typeof dias === 'object') {
      // Verificar que tiene keys de día
      const keys = Object.keys(dias);
      if (keys.some((k) => WEEKDAYS_FULL.includes(k))) {
        return dias;
      }
    }
    // Es array de registros
    if (Array.isArray(dias)) {
      const result = {};
      const dayMap = {
        'L': 'L', 'LUNES': 'L',
        'M': 'M', 'MARTES': 'M',
        'X': 'X', 'MIERCOLES': 'X', 'MIÉRCOLES': 'X',
        'J': 'J', 'JUEVES': 'J',
        'V': 'V', 'VIERNES': 'V',
        'S': 'S', 'SABADO': 'S', 'SÁBADO': 'S',
        'D': 'D', 'DOMINGO': 'D',
      };
      dias.forEach((reg) => {
        const raw = String(reg.dia_semana ?? reg.diaSemana ?? reg.dia ?? reg.day ?? '').trim().toUpperCase();
        const key = dayMap[raw] || (WEEKDAYS_FULL.includes(raw) ? raw : null);
        if (!key) return;
        const check = reg.check !== undefined ? Boolean(reg.check) : true; // si hay registro, asumimos que opera
        const kg = Number(reg.kg ?? reg.kg_asignado ?? reg.kgAsignado ?? 0);
        const m3 = Number(reg.m3 ?? reg.m3_asignado ?? reg.m3Asignado ?? 0);
        result[key] = { check, kg, m3 };
      });
      return result;
    }
    return {};
  };

  WEEKDAYS_FULL.forEach((dia) => {
    let sumKg = 0;
    let sumM3 = 0;
    (vuelo.clientes || []).forEach((c) => {
      const diasNorm = normalizeDias(c.dias);
      const d = diasNorm[dia];
      if (d && (d.check === true || d.check === 1)) {
        sumKg += Number(d.kg ?? d.kg_asignado ?? d.kgAsignado ?? 0);
        sumM3 += Number(d.m3 ?? d.m3_asignado ?? d.m3Asignado ?? 0);
      }
    });
    // Capacidad: soporta CapTons/capTons/cap_tons y CapM3/capM3/cap_m3
    const capTons = Number(vuelo.CapTons ?? vuelo.capTons ?? vuelo.cap_tons ?? 0);
    const capM3   = Number(vuelo.CapM3   ?? vuelo.capM3   ?? vuelo.cap_m3   ?? 0);
    const capKg = capTons * 1000;
    focKg[dia] = capKg > 0 ? Math.round((sumKg / capKg) * 100) + '%' : '0%';
    focVol[dia] = capM3 > 0 ? Math.round((sumM3 / capM3) * 100) + '%' : '0%';
  });
  return { focKg, focVol };
}

// Los vuelos se cargan desde el backend via API.
// Este export se mantiene vacío para compatibilidad.
export const vuelos = [];
